import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    CurrencyInr,
    Crown,
    Lightning,
    Sparkle,
    Users as UsersIcon,
    ArrowUpRight,
    Eye,
    Download,
    MagnifyingGlass,
    Clock,
    Warning,
} from "@phosphor-icons/react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return "—"; }
};
const daysUntil = (iso) => {
    if (!iso) return 0;
    try {
        const diff = new Date(iso).getTime() - Date.now();
        return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
    } catch { return 0; }
};

export default function AdminRevenue() {
    const [tab, setTab] = useState("overview");
    const [summary, setSummary] = useState(null);
    const [attribution, setAttribution] = useState(null);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [renewals, setRenewals] = useState([]);
    const [q, setQ] = useState("");
    const [reveals, setReveals] = useState({}); // uid → {email, phone}
    const [loading, setLoading] = useState(false);

    const loadOverview = async () => {
        try {
            const [s, p, a] = await Promise.all([
                api.get("/admin/revenue/summary"),
                api.get("/admin/revenue/products"),
                api.get("/admin/revenue/attribution", { params: { window: "month" } }),
            ]);
            setSummary(s.data);
            setProducts(p.data.products || []);
            setAttribution(a.data);
        } catch {
            toast.error("Failed to load revenue overview");
        }
    };
    const loadCustomers = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/revenue/customers", { params: { q, limit: 100 } });
            setCustomers(r.data.items || []);
        } catch {
            toast.error("Failed to load customers");
        } finally { setLoading(false); }
    };
    const loadRenewals = async () => {
        try {
            const r = await api.get("/admin/revenue/renewals", { params: { days: 7 } });
            setRenewals(r.data.items || []);
        } catch {
            toast.error("Failed to load renewals");
        }
    };

    useEffect(() => { loadOverview(); }, []);
    useEffect(() => {
        if (tab === "customers") loadCustomers();
        if (tab === "renewals") loadRenewals();
    }, [tab]);  // eslint-disable-line react-hooks/exhaustive-deps

    const revealPII = async (uid) => {
        try {
            const { data } = await api.post(`/admin/revenue/customers/${uid}/reveal`);
            setReveals((s) => ({ ...s, [uid]: data }));
            toast.success("PII reveal logged in audit trail");
        } catch {
            toast.error("Couldn't reveal — check permissions");
        }
    };
    const downloadCSV = async () => {
        try {
            const resp = await api.get("/admin/revenue/customers.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "paying-customers.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV exported · Download logged");
        } catch {
            toast.error("Export failed");
        }
    };

    const TABS = [
        { key: "overview", label: "Overview" },
        { key: "products", label: "Products" },
        { key: "customers", label: "Paying customers" },
        { key: "renewals", label: "Renewal watch" },
    ];

    return (
        <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-revenue-page">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        Monetization
                    </p>
                    <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                        Revenue
                    </h1>
                    <p className="text-sm text-[#525252] mt-1">
                        Everything sold across the funnel. Post multi-tenant, this moves to Super-Admin.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-[#E5E5E5] mb-6 flex gap-6 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`admin-revenue-tab-${t.key}`}
                        className={`text-xs font-black uppercase tracking-wider pb-3 border-b-2 transition-colors whitespace-nowrap ${
                            tab === t.key
                                ? "border-ink text-ink"
                                : "border-transparent text-[#A3A3A3] hover:text-ink"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && summary && (
                <div data-testid="admin-revenue-overview">
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                        {[
                            { label: "Today", data: summary.today, tone: "text-splash" },
                            { label: "Last 7 days", data: summary.week, tone: "text-cobalt" },
                            { label: "Last 30 days", data: summary.month, tone: "text-emerald-600" },
                            { label: "All-time", data: summary.all, tone: "text-ink" },
                        ].map(({ label, data, tone }) => (
                            <div key={label} data-testid={`admin-revenue-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}
                                className="bg-white border border-[#E5E5E5] p-5">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">
                                    {label}
                                </p>
                                <p className={`font-heading text-3xl font-black tracking-tighter ${tone}`}>
                                    {fmtINR(data?.revenue_inr)}
                                </p>
                                <p className="text-[11px] text-[#525252] mt-1">
                                    {data?.units || 0} {data?.units === 1 ? "order" : "orders"}
                                </p>
                            </div>
                        ))}
                    </div>
                    {/* Attribution — Candidate vs Agency revenue */}
                    {attribution && (
                        <div data-testid="admin-revenue-attribution" className="mb-8">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    Revenue attribution · Last 30 days
                                </p>
                                <p className="text-[10px] font-mono text-[#A3A3A3]">
                                    Total {fmtINR(attribution.total_revenue_inr)}
                                </p>
                            </div>
                            <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6">
                                <AttributionSplitBar buckets={attribution.buckets} />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                                    {["candidate", "agency"].map((k) => {
                                        const b = attribution.buckets[k];
                                        const tone = k === "candidate" ? "text-cobalt" : "text-splash";
                                        const dotClass = k === "candidate" ? "bg-cobalt" : "bg-splash";
                                        return (
                                            <div key={k} data-testid={`attribution-bucket-${k}`}
                                                 className="border border-[#F5F5F5] p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
                                                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                                            {k === "candidate" ? "Candidate-paid" : "Agency-paid"}
                                                        </p>
                                                    </div>
                                                    <p className={`text-[10px] font-black ${tone}`}>{b.share_pct}%</p>
                                                </div>
                                                <p className={`font-heading text-2xl font-black tracking-tighter ${tone}`}>
                                                    {fmtINR(b.revenue_inr)}
                                                </p>
                                                <p className="text-[10px] text-[#A3A3A3] mt-1">
                                                    {b.units} {b.units === 1 ? "order" : "orders"}
                                                </p>
                                                {b.products.length > 0 && (
                                                    <ul className="mt-3 space-y-1 border-t border-[#F5F5F5] pt-2">
                                                        {b.products.slice(0, 4).map((p) => (
                                                            <li key={p.key} className="flex items-center justify-between text-[11px]">
                                                                <span className="text-[#525252] truncate">{p.label}</span>
                                                                <span className="text-ink font-mono">{fmtINR(p.revenue_inr)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {b.products.length === 0 && (
                                                    <p className="text-[10px] text-[#A3A3A3] italic mt-2">
                                                        {k === "agency"
                                                            ? "No agency subscriptions yet."
                                                            : "No candidate orders yet."}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active-users cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        <div className="bg-ink text-white p-5 flex items-center gap-4">
                            <Crown size={22} weight="fill" className="text-splash" />
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-wider text-white/60">
                                    Active Pro passes
                                </p>
                                <p className="font-heading text-2xl font-black tracking-tight">
                                    {summary.active_pro_users || 0}
                                </p>
                            </div>
                        </div>
                        <div className="bg-white border border-[#E5E5E5] p-5 flex items-center gap-4">
                            <Lightning size={22} weight="fill" className="text-splash" />
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                    Active application boosts
                                </p>
                                <p className="font-heading text-2xl font-black tracking-tight text-ink">
                                    {summary.active_application_boosts || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    {products.length > 0 && (
                        <>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                                Product breakdown
                            </p>
                            <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                                {products.map((p) => (
                                    <div key={p.key} className="px-4 sm:px-5 py-4 flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 bg-ink text-splash flex items-center justify-center shrink-0">
                                                {p.key === "pro_pass" ? <Crown size={16} weight="fill" /> :
                                                 p.key === "app_boost" ? <Lightning size={16} weight="fill" /> :
                                                 <Sparkle size={16} weight="fill" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-heading text-sm font-bold tracking-tight truncate">{p.label}</p>
                                                    <PaidByPill paidBy={p.paid_by} />
                                                </div>
                                                <p className="text-[11px] text-[#A3A3A3]">
                                                    {p.unit_price_inr ? `List price ${fmtINR(p.unit_price_inr)} · ` : ""}AOV {fmtINR(p.avg_order_value_inr)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-heading text-lg font-black tracking-tight">
                                                {fmtINR(p.all_time.revenue_inr)}
                                            </p>
                                            <p className="text-[11px] text-[#525252]">
                                                {p.all_time.units} sold · <span className="text-emerald-600">{p.week.units} this week</span>
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {tab === "products" && (
                <div data-testid="admin-revenue-products">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {products.map((p) => (
                            <div key={p.key} className="bg-white border border-[#E5E5E5] p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 bg-ink text-splash flex items-center justify-center">
                                        {p.key === "pro_pass" ? <Crown size={14} weight="fill" /> :
                                         p.key === "app_boost" ? <Lightning size={14} weight="fill" /> :
                                         <Sparkle size={14} weight="fill" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-heading text-base font-bold tracking-tight truncate">{p.label}</p>
                                        <PaidByPill paidBy={p.paid_by} />
                                    </div>
                                </div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                    {p.unit_price_inr ? "List price" : "Avg order value"}
                                </p>
                                <p className="font-heading text-2xl font-black tracking-tighter mb-3">
                                    {fmtINR(p.unit_price_inr || p.avg_order_value_inr)}
                                </p>
                                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#F5F5F5]">
                                    {[
                                        ["7d", p.week],
                                        ["30d", p.month],
                                        ["All", p.all_time],
                                    ].map(([label, d]) => (
                                        <div key={label}>
                                            <p className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">{label}</p>
                                            <p className="font-heading text-sm font-black tracking-tight">{fmtINR(d.revenue_inr)}</p>
                                            <p className="text-[10px] text-[#525252]">{d.units} sold</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === "customers" && (
                <div data-testid="admin-revenue-customers">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white border border-[#E5E5E5] px-3">
                            <MagnifyingGlass size={14} weight="bold" className="text-[#A3A3A3]" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && loadCustomers()}
                                placeholder="Search by name, email, phone…"
                                data-testid="admin-revenue-customers-search"
                                className="w-full bg-transparent py-2.5 text-sm focus:outline-none placeholder:text-[#A3A3A3]"
                            />
                        </div>
                        <button
                            onClick={loadCustomers}
                            className="border border-ink hover:bg-ink hover:text-white text-ink px-4 py-2.5 text-xs font-black uppercase tracking-wider transition-colors"
                        >
                            Search
                        </button>
                        <button
                            onClick={downloadCSV}
                            data-testid="admin-revenue-csv-export"
                            className="bg-ink hover:bg-cobalt text-white px-4 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                        >
                            <Download size={12} weight="bold" /> Export CSV
                        </button>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mb-3 flex items-center gap-1.5">
                        <Warning size={11} weight="fill" className="text-splash" />
                        PII masked by default. Click the eye to reveal — every reveal is written to the audit log.
                    </p>

                    {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
                    {!loading && customers.length === 0 && (
                        <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                            <UsersIcon size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                            <p className="text-sm text-[#525252]">No paying customers match this search yet.</p>
                        </div>
                    )}
                    {customers.length > 0 && (
                        <>
                        {/* Mobile cards */}
                        <div className="sm:hidden space-y-3">
                            {customers.map((c) => {
                                const rev = reveals[c.user_id];
                                return (
                                    <div key={`m-${c.user_id}`} data-testid={`admin-revenue-customer-card-${c.user_id}`} className="bg-white border border-[#E5E5E5] p-4">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="min-w-0">
                                                <p className="font-heading font-bold text-ink truncate">{c.name}</p>
                                                <p className="text-[10px] text-[#A3A3A3]">
                                                    Joined {fmtDate(c.signup_at)}
                                                </p>
                                                {c.pro_until && daysUntil(c.pro_until) > 0 && (
                                                    <p className="text-[10px] text-splash font-bold mt-0.5">Pro · {daysUntil(c.pro_until)}d left</p>
                                                )}
                                            </div>
                                            <p className="font-heading font-black text-right shrink-0">{fmtINR(c.total_spent_inr)}</p>
                                        </div>
                                        <div className="pt-2 border-t border-[#F5F5F5] space-y-2 text-xs">
                                            <div>
                                                <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] mb-0.5">Contact</p>
                                                <p className="font-mono text-ink">{rev?.email || c.email_masked}</p>
                                                <p className="font-mono text-[#A3A3A3]">{rev?.phone || c.phone_masked}</p>
                                            </div>
                                            {c.products.length > 0 && (
                                                <div>
                                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] mb-1">Products</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {c.products.map((p) => (
                                                            <span key={p.label} className="text-[10px] bg-[#F5F5F5] px-2 py-0.5 font-bold">
                                                                {p.label}: {p.units}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between pt-1">
                                                <p className="text-[10px] text-[#525252]">Last: {fmtDate(c.last_purchase_at)}</p>
                                                {!rev && (c.has_email || c.has_phone) ? (
                                                    <button
                                                        onClick={() => revealPII(c.user_id)}
                                                        data-testid={`admin-revenue-reveal-card-${c.user_id}`}
                                                        className="text-cobalt hover:text-cobalt-dark text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1"
                                                    >
                                                        <Eye size={12} weight="bold" /> Reveal
                                                    </button>
                                                ) : rev ? (
                                                    <span className="text-[10px] text-emerald-600 font-black uppercase tracking-wider">Revealed</span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Desktop table */}
                        <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                            <table className="w-full text-sm min-w-[860px]">
                                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                                    <tr className="text-left">
                                        {["Customer", "Contact", "Products", "Total spent", "Last purchase", ""].map((h) => (
                                            <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((c) => {
                                        const rev = reveals[c.user_id];
                                        return (
                                            <tr key={c.user_id} data-testid={`admin-revenue-customer-${c.user_id}`} className="border-b border-[#F5F5F5]">
                                                <td className="px-4 py-3">
                                                    <p className="font-heading font-bold text-ink">{c.name}</p>
                                                    <p className="text-[10px] text-[#A3A3A3]">
                                                        Joined {fmtDate(c.signup_at)}
                                                        {c.pro_until && daysUntil(c.pro_until) > 0 && (
                                                            <span className="ml-2 text-splash font-bold">· Pro · {daysUntil(c.pro_until)}d left</span>
                                                        )}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-3 text-xs">
                                                    <p className="text-ink font-mono">{rev?.email || c.email_masked}</p>
                                                    <p className="text-[#A3A3A3] font-mono">{rev?.phone || c.phone_masked}</p>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {c.products.map((p) => (
                                                            <span key={p.label} className="text-[10px] bg-[#F5F5F5] px-2 py-0.5 font-bold">
                                                                {p.label}: {p.units}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 font-heading font-black">{fmtINR(c.total_spent_inr)}</td>
                                                <td className="px-4 py-3 text-xs text-[#525252]">{fmtDate(c.last_purchase_at)}</td>
                                                <td className="px-4 py-3 text-right">
                                                    {!rev && (c.has_email || c.has_phone) && (
                                                        <button
                                                            onClick={() => revealPII(c.user_id)}
                                                            data-testid={`admin-revenue-reveal-${c.user_id}`}
                                                            className="text-[#525252] hover:text-cobalt text-xs inline-flex items-center gap-1"
                                                            title="Reveal contact info (writes to audit log)"
                                                        >
                                                            <Eye size={12} weight="bold" /> Reveal
                                                        </button>
                                                    )}
                                                    {rev && (
                                                        <span className="text-[10px] text-emerald-600 font-black uppercase tracking-wider">
                                                            Revealed
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}
                </div>
            )}

            {tab === "renewals" && (
                <div data-testid="admin-revenue-renewals">
                    <p className="text-sm text-[#525252] mb-4">
                        Pro passes expiring in the next 7 days. Outreach opportunity — offer them a renewal discount before churn.
                    </p>
                    {renewals.length === 0 ? (
                        <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                            <Clock size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                            <p className="text-sm text-[#525252]">No Pro passes expiring in the next 7 days.</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                            {renewals.map((r) => (
                                <div key={r.user_id} data-testid={`admin-revenue-renewal-${r.user_id}`}
                                    className="px-4 sm:px-5 py-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-heading font-bold tracking-tight truncate">{r.name}</p>
                                        <p className="text-[11px] text-[#A3A3A3] font-mono">{r.email_masked} · {r.phone_masked}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-wider ${
                                            r.days_left <= 2 ? "bg-red-100 text-red-800" :
                                            r.days_left <= 4 ? "bg-splash text-ink" :
                                            "bg-[#F5F5F5] text-[#525252]"
                                        }`}>
                                            <ArrowUpRight size={10} weight="bold" />
                                            {r.days_left} {r.days_left === 1 ? "day" : "days"} left
                                        </span>
                                        <p className="text-[10px] text-[#A3A3A3] mt-1">Expires {fmtDate(r.pro_until)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Candidate/Agency attribution split bar ----------
function PaidByPill({ paidBy }) {
    const isAgency = paidBy === "agency";
    return (
        <span data-testid={`paid-by-pill-${paidBy}`}
              className={`inline-flex text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border ${
                  isAgency
                      ? "text-splash bg-splash/10 border-splash/30"
                      : "text-cobalt bg-cobalt/10 border-cobalt/30"
              }`}>
            {isAgency ? "Agency-paid" : "Candidate-paid"}
        </span>
    );
}

function AttributionSplitBar({ buckets }) {
    const cand = buckets?.candidate?.share_pct || 0;
    const agy = buckets?.agency?.share_pct || 0;
    const empty = cand === 0 && agy === 0;
    return (
        <div className="w-full">
            <div className="h-3 bg-[#F5F5F5] w-full overflow-hidden flex" data-testid="attribution-split-bar">
                {empty ? (
                    <div className="h-full w-full bg-[#F5F5F5]" />
                ) : (
                    <>
                        {cand > 0 && (
                            <div className="h-full bg-cobalt transition-all" style={{ width: `${cand}%` }} title={`Candidate ${cand}%`} />
                        )}
                        {agy > 0 && (
                            <div className="h-full bg-splash transition-all" style={{ width: `${agy}%` }} title={`Agency ${agy}%`} />
                        )}
                    </>
                )}
            </div>
            {empty && (
                <p className="text-[10px] text-[#A3A3A3] mt-2 text-center italic">
                    No paid orders in this window yet.
                </p>
            )}
        </div>
    );
}

